import { range, sum } from 'lodash';
import { returnZero } from '@ardoq/common-helpers';
import { IconSize } from '@ardoq/icons';
import { OTHER_LABEL_MAX_LINES, Size, formatOtherLabel } from '@ardoq/graph';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { wrapString } from 'tabview/blocks/misc/string';
import {
  ComponentMatrixGroup,
  ComponentMatrixGroupOwnerType,
  HeaderCellModel,
} from '../../types';
import {
  ComponentMatrix2Group,
  GroupWithGridInfo,
  RowElementModel,
  ViewpointModeComponentMatrixViewModel,
} from '../types';
import {
  calculateRowHeaderBottomOffset,
  calculateRowHeaderTopOffset,
  hasChildren,
  isFirstChild,
  isLastChild,
} from '../../util';
import {
  BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
  CARD_COLUMN_GROUP_BOTTOM_MARGIN,
  CARD_COLUMN_GROUP_RIGHT_MARGIN,
  CARD_COLUMN_GROUP_TOP_MARGIN,
  CARD_COLUMN_SIBLING_GAP,
  BODY_CELL_CONTAINER_VERTICAL_PADDING,
  CARD_SIBLING_GAP,
  CONTEXT_OUTLINE_OUTSET,
  COMPONENT_MATRIX_LABEL_FONT,
  COMPONENT_MATRIX_LABEL_FONT_BOLD,
  LEVEL_HEADER_CARD_HEIGHT,
  MIN_HEADER_CELL_HEIGHT,
  COMPONENT_HIERARCHY_PADDING,
  HEADER_CELL_VERTICAL_PADDING,
  ICON_WIDTH,
  ITEM_WIDTH_WITHOUT_TEXT,
  LEVEL_HEADER_CELL_HORIZONTAL_PADDING,
  CARD_GROUP_BOTTOM_MARGIN,
  CARD_GROUP_HORIZONTAL_MARGIN,
  CARD_GROUP_TOP_MARGIN,
  LABEL_LINE_HEIGHT,
  MARGIN_PER_LEVEL,
  MAX_COLUMN_HEADER_WIDTH,
  MAX_ROW_HEADER_WIDTH,
  MIN_COLUMN_HEADER_WIDTH,
  NONE_HEADER_LABEL,
  ROW_HEADER_LABEL_HEIGHT,
  ROW_HEADER_STRIPE_HEIGHT,
} from '../consts';
import { measureLevelLabel } from '../../viewModel$/measureLabels';

export const buildVisibleLeafCounts = (
  rootGroups: Iterable<ComponentMatrixGroup>,
  map = new Map<ComponentMatrixGroup, number>()
): Map<ComponentMatrixGroup, number> => {
  for (const group of rootGroups) {
    if (group.children.size && !group.isHidden && group.isExpanded) {
      buildVisibleLeafCounts(group.children.values(), map);
      map.set(
        group,
        [...group.children.values()].reduce(
          (acc, child) => acc + (map.get(child) ?? 1),
          0
        )
      );
    } else {
      map.set(group, 1);
    }
  }
  return map;
};

/**
 * Recursively traverse root column groups and assign absolute gridColumn and
 * colSpan to every group at every depth level. Groups at depth > 0 inherit the
 * correct starting column from their parent — a flat per-level augment that
 * restarts from column 1 would place child groups at the wrong tracks when a
 * root-level leaf group (e.g. "none") precedes an expanded group.
 */
export const buildColumnsByDepthWithGridInfo = (
  rootGroups: ComponentMatrix2Group[],
  colorsByComponentId: ReadonlyMap<string, ComponentMatrix2Group>
): GroupWithGridInfo[][] => {
  const leafCounts = buildVisibleLeafCounts(rootGroups);
  const result: GroupWithGridInfo[][] = [];

  const traverse = (
    groups: ComponentMatrixGroup[],
    depth: number,
    startColumn: number
  ): number => {
    if (result.length <= depth) result.push([]);
    let column = startColumn;
    for (const group of groups) {
      // colSpan matches leafCounts used by getVisibleItemIndex so header position
      // and body-cell track index always agree. ?? 1 guards against a missing entry.
      const colSpan = leafCounts.get(group) ?? 1;
      const groupColors = colorsByComponentId.get(group.address.at(-1) ?? '');
      const isListValueColumn =
        group.ownerType !==
          ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT &&
        group.ownerType !== ComponentMatrixGroupOwnerType.EMPTY &&
        !group.isHidden &&
        group.label !== COMPONENT_HIERARCHY_PADDING;
      result[depth].push({
        ...group,
        gridColumn: column,
        colSpan,
        fillColor: groupColors?.fillColor ?? '',
        isDark: groupColors?.isDark ?? false,
        ...(isListValueColumn
          ? { labelLineCount: getMaxWrappedLinesForColumn(group) }
          : {}),
      });
      // Recurse into ALL non-hidden children — including PADDING groups.
      // PADDING groups occupy tracks (they're counted by buildVisibleLeafCounts and
      // by getVisibleItemIndex), so they MUST advance the column counter here.
      // Filtering them out would shift sibling groups left, misaligning headers
      // with body cells. componentMatrixHeaderCell returns null for PADDING, so
      // they render nothing but still contribute to the grid-template-columns.
      const visibleChildren =
        group.isExpanded && group.children.size
          ? [...group.children.values()].filter(c => !c.isHidden)
          : [];
      if (visibleChildren.length) {
        traverse(visibleChildren, depth + 1, column);
      }
      column += colSpan;
    }
    return column;
  };

  traverse(rootGroups, 0, 1);
  return result;
};

/** CardStripe has box-sizing: border-box with 1px borders on both left AND right. */
const COLUMN_HEADER_HORIZONTAL_BORDER = 2;
/** Left + right padding inside the CardStripe (s8 = 8px each side). */
const COLUMN_HEADER_CARD_HORIZONTAL_PADDING = 16;
/** ComponentMatrixListValueHeaderCellContainer: padding s8 (8px) each side + 1px border each side. */
const ROW_HEADER_LIST_VALUE_CELL_HORIZONTAL_OVERHEAD = 8 * 2 + 1 * 2;
/**
 * List-value column header padding: s8 each side + 1px border each side + 4px safety buffer for
 * sub-pixel rendering inaccuracies. See getHeaderColumnWidth for full explanation.
 */
const LIST_VALUE_COLUMN_HEADER_HORIZONTAL_OVERHEAD = 8 * 2 + 1 * 2 + 4;
/** Vertical overhead of ComponentMatrixListValueHeaderCellContainer: 8px padding each side + 1px border each side. */
const LIST_VALUE_CELL_VERTICAL_OVERHEAD = 8 * 2 + 1 * 2;

const EXPANDER_WIDTH = IconSize.MEDIUM;
/**
 * UrlFieldValueIcon uses IconSize.SMALL (16px). With the 4px flex gap before it,
 * items that have URL values need this extra space to avoid truncating the label.
 */
const URL_ICON_OVERHEAD = IconSize.SMALL + 4;

/** Max lines before truncation with ellipsis on the last line. */
const MAX_WRAPPED_LINES = 3;
/** Vertical overhead per body item: 4px top padding + 4px bottom padding.
 * `ComponentItemContainer` has no border — its selection/context outlines are
 * `box-shadow`, which doesn't take layout space. */
const ITEM_VERTICAL_OVERHEAD = 8;
/** Vertical overhead per badge: 2px padding-top + 2px padding-bottom + 1px border-top + 1px border-bottom. */
const OTHER_LABEL_BADGE_VERTICAL_OVERHEAD = 6;
/** Horizontal overhead per badge: 8px padding-left + 8px padding-right + 1px border-left + 1px border-right. */
const OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD = 18;
/** padding-top of OtherLabelsContainer (sits between the main label and the badges). */
const OTHER_LABELS_CONTAINER_TOP_PADDING = 4;
/** padding-bottom of OtherLabelsContainer (below the last badge). */
const OTHER_LABELS_CONTAINER_BOTTOM_PADDING = 4;
/** gap between badges in OtherLabelsContainer. */
const OTHER_LABELS_GAP = 4;
/** Padding of the row header CardContent (s8 = 8px, all sides — applies to both height and width calculations). */
const ROW_HEADER_CARD_PADDING = 16;
/** Flex gap between items in CardContent (s4 = 4px). */
const ROW_HEADER_FLEX_GAP = 4;
/** Border of the row header card (1px each side, box-sizing: border-box — the border is inside the declared width/height). */
const ROW_HEADER_CARD_BORDER = 2;
/** Horizontal extras (card padding + border + 2× flex gap) added to a component row
 * header's content width to land on its rendered grid column width.
 * `getHeaderRowWidth` returns text+icon+expander only; without this overhead
 * the outer rowHeadersAreaWidth ends up narrower than what
 * `getComponentRowHeaderHeight` assumed when wrapping the label, producing
 * row tracks too short to fit their wrapped labels. */
const COMPONENT_ROW_HEADER_CARD_OVERHEAD =
  ROW_HEADER_CARD_PADDING + ROW_HEADER_CARD_BORDER + 2 * ROW_HEADER_FLEX_GAP;
/** Vertical padding of the column header CardStripe (s8 = 8px, both sides). */
const COLUMN_HEADER_VERTICAL_PADDING = 16;

/**
 * Pre-computes the wrapped text lines for each column-header badge given the
 * column width. Returns the same lines that `getColumnHeaderOtherLabelsHeight`
 * uses internally, so the rendered badge height is guaranteed to match the
 * measurement when callers pass the result to `OtherLabel`'s `wrappedLines` prop.
 */
export const computeColumnHeaderOtherLabelWrappedLines = (
  column: ComponentMatrixGroup
): string[][] => {
  const clampedWidth = Math.min(
    MAX_COLUMN_HEADER_WIDTH,
    Math.max(MIN_COLUMN_HEADER_WIDTH, getHeaderColumnWidth(column))
  );
  const textAvailable = Math.max(
    0,
    clampedWidth -
      COLUMN_HEADER_CARD_HORIZONTAL_PADDING -
      COLUMN_HEADER_HORIZONTAL_BORDER
  );
  const availableBadgeTextWidth = Math.max(
    1,
    textAvailable - OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
  );
  return column.otherLabels.map(otherLabel =>
    wrapString(
      formatOtherLabel(otherLabel),
      availableBadgeTextWidth,
      measureText,
      OTHER_LABEL_MAX_LINES
    )
  );
};

/**
 * Pre-computes the wrapped text lines for each row-header badge given the card
 * width. Returns the same lines that `getOtherLabelsHeight` uses internally, so
 * the rendered badge height is guaranteed to match the measurement when callers
 * pass the result to `OtherLabel`'s `wrappedLines` prop.
 */
export const computeRowHeaderOtherLabelWrappedLines = (
  otherLabels: ComponentMatrixGroup['otherLabels'],
  cardWidth: number
): string[][] => {
  const badgesAvailableWidth = Math.max(
    0,
    cardWidth - ROW_HEADER_CARD_PADDING - ROW_HEADER_CARD_BORDER
  );
  const availableBadgeTextWidth = Math.max(
    1,
    badgesAvailableWidth - OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
  );
  return otherLabels.map(otherLabel =>
    wrapString(
      formatOtherLabel(otherLabel),
      availableBadgeTextWidth,
      measureText,
      OTHER_LABEL_MAX_LINES
    )
  );
};

/**
 * Height of the OtherLabelsContainer (badges) for a given available text width.
 * Returns 0 when otherLabels is empty.
 */
const getOtherLabelsHeight = (
  otherLabels: ComponentMatrixGroup['otherLabels'],
  availableTextWidth: number
): number => {
  if (otherLabels.length === 0) return 0;
  const availableBadgeTextWidth = Math.max(
    1,
    availableTextWidth - OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
  );
  const badgesHeight =
    otherLabels.reduce((total, otherLabel) => {
      const lines = Math.max(
        1,
        wrapString(
          formatOtherLabel(otherLabel),
          availableBadgeTextWidth,
          measureText,
          OTHER_LABEL_MAX_LINES
        ).length
      );
      return (
        total +
        lines * LABEL_LINE_HEIGHT +
        OTHER_LABEL_BADGE_VERTICAL_OVERHEAD +
        OTHER_LABELS_GAP
      );
    }, 0) - OTHER_LABELS_GAP;
  return (
    OTHER_LABELS_CONTAINER_TOP_PADDING +
    badgesHeight +
    OTHER_LABELS_CONTAINER_BOTTOM_PADDING
  );
};

/**
 * canvasLabelMeasurer sets the font once on an OffscreenCanvas and wraps measureText in
 * a FIFO cache — no per-call font re-set or RegExp allocation. Math.ceil is kept because
 * Chrome rounds up when deciding whether to apply the text-overflow ellipsis.
 */
const measureText = canvasLabelMeasurer(COMPONENT_MATRIX_LABEL_FONT);
const measureTextBold = canvasLabelMeasurer(COMPONENT_MATRIX_LABEL_FONT_BOLD);
const getTextWidth = (text: string) => Math.ceil(measureText(text));
/**
 * List-value column and row headers use ListValueLabel which applies text2Bold (font-weight: 600).
 * Measuring with the default body weight (400) would underestimate the rendered width.
 */
const getTextWidthBold = (text: string) => Math.ceil(measureTextBold(text));

const getColumnCount = (rowModels: RowElementModel[]) =>
  rowModels.length === 0
    ? 0
    : sum(rowModels[0].bodyCells.map(bodyCell => bodyCell.columnSpan));
const getRowCount = (rowModels: RowElementModel[]) =>
  rowModels.length === 0
    ? 0
    : sum(
        rowModels.map(rowModel =>
          rowModel.bodyCells.length === 0 ? 0 : rowModel.bodyCells[0].rowSpan
        )
      );

const MIN_COLUMN_WIDTH = 44;
const MAX_COLUMN_WIDTH = 480;
// Visible width of the grey stripe rendered for an empty (hierarchy-padding)
// column. Such columns carry no components, so collapse them to a thin stripe
// instead of a full MIN_COLUMN_WIDTH track — a wide empty grey box reads as a
// missing/broken column (ARD-32761). The `BodyCellItemsContainer` insets the
// grey by BODY_CELL_CONTAINER_HORIZONTAL_MARGIN on each side, so the track must
// be the stripe width plus those margins for the grey itself to be this wide.
const EMPTY_COLUMN_STRIPE_WIDTH = 8;
export const EMPTY_COLUMN_WIDTH =
  EMPTY_COLUMN_STRIPE_WIDTH + 2 * BODY_CELL_CONTAINER_HORIZONTAL_MARGIN;

type BodyCell = RowElementModel['bodyCells'][number];

/**
 * Total rendered width of a body cell (the full cell, not divided by colSpan).
 * Single source of truth shared by `measureCells` (row-height measurement) and
 * `augmentBodyCellItemsWithOtherLabelWrappedLines` (badge wrapping), so the two
 * can never disagree.
 */
/**
 * Extra cell width reserved for a context item's box-shadow outline, applied as
 * margin on the `ComponentItemContainer`. The item's content area (label +
 * badges) does NOT gain this width — the margin consumes it — so it must be
 * subtracted back out when sizing badge text (see getBodyCellBadgeTextWidth).
 */
const getBodyCellContextOutlineExtent = (bodyCell: BodyCell): number =>
  bodyCell.items.some(item => item.isContext) ? 2 * CONTEXT_OUTLINE_OUTSET : 0;

const getBodyCellTotalWidth = (bodyCell: BodyCell): number => {
  const contextOutlineExtent = getBodyCellContextOutlineExtent(bodyCell);
  return (
    (bodyCell.items.length === 0
      ? EMPTY_COLUMN_WIDTH
      : Math.max(
          MIN_COLUMN_WIDTH,
          Math.min(
            MAX_COLUMN_WIDTH,
            Math.max(
              ...bodyCell.items.map(
                ({ componentLabel, otherLabels, urlFieldValues }) => {
                  const mainLabelWidth =
                    getTextWidth(componentLabel) +
                    (urlFieldValues?.length ? URL_ICON_OVERHEAD : 0);
                  const widestBadge = otherLabels.reduce(
                    (max, label) =>
                      Math.max(
                        max,
                        getTextWidth(formatOtherLabel(label)) +
                          OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD -
                          ICON_WIDTH -
                          4
                      ),
                    0
                  );
                  return Math.max(mainLabelWidth, widestBadge);
                }
              )
            ) + ITEM_WIDTH_WITHOUT_TEXT
          )
        )) + contextOutlineExtent
  );
};

/**
 * Width available to an OtherLabel badge's text within a body cell. The
 * context-outline extent is subtracted because it is reserved as margin around
 * the context item, not usable content width.
 */
const getBodyCellBadgeTextWidth = (
  totalCellWidth: number,
  contextOutlineExtent: number
): number =>
  Math.max(
    1,
    Math.max(
      0,
      totalCellWidth - contextOutlineExtent - ITEM_WIDTH_WITHOUT_TEXT
    ) +
      ICON_WIDTH +
      4 -
      OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
  );

/**
 * Pre-computes the wrapped text lines for each body-item badge at the same
 * width the cell height is measured at. Passing these to `OtherLabel`'s
 * `wrappedLines` prop guarantees the rendered badge height matches the
 * measurement, so badges are never clipped by the cell's `overflow: hidden`
 * (ARD-33210).
 */
export const augmentBodyCellItemsWithOtherLabelWrappedLines = (
  rowModels: RowElementModel[]
): RowElementModel[] =>
  rowModels.map(rowModel => ({
    ...rowModel,
    bodyCells: rowModel.bodyCells.map(bodyCell => {
      const availableBadgeTextWidth = getBodyCellBadgeTextWidth(
        getBodyCellTotalWidth(bodyCell),
        getBodyCellContextOutlineExtent(bodyCell)
      );
      return {
        ...bodyCell,
        items: bodyCell.items.map(item =>
          item.otherLabels.length > 0
            ? {
                ...item,
                otherLabelWrappedLines: item.otherLabels.map(otherLabel =>
                  wrapString(
                    formatOtherLabel(otherLabel),
                    availableBadgeTextWidth,
                    measureText,
                    OTHER_LABEL_MAX_LINES
                  )
                ),
              }
            : item
        ),
      };
    }),
  }));

export const measureCells = (rowModels: RowElementModel[]) => {
  const columnCount = getColumnCount(rowModels);
  const rowCount = getRowCount(rowModels);
  // create the empty matrix
  const result: Size[][] = range(rowCount).map(() =>
    range(columnCount).map(() => ({ width: NaN, height: NaN }))
  );

  for (
    let rowModelIndex = 0;
    rowModelIndex < rowModels.length;
    rowModelIndex++
  ) {
    const rowModel = rowModels[rowModelIndex];
    for (
      let bodyCellIndex = 0;
      bodyCellIndex < rowModel.bodyCells.length;
      bodyCellIndex++
    ) {
      const bodyCell = rowModel.bodyCells[bodyCellIndex];
      // When a body cell contains the context component, the column it lives
      // in and the row it lives in each need an extra
      // `2 * CONTEXT_OUTLINE_OUTSET` to host the box-shadow context highlight
      // applied as margin on the context `ComponentItemContainer` (see
      // atoms.ts). Non-context cells stay at their natural size.
      const contextOutlineExtent = getBodyCellContextOutlineExtent(bodyCell);
      const totalCellWidth = getBodyCellTotalWidth(bodyCell);
      const currentCellWidth = totalCellWidth / bodyCell.columnSpan;
      const availableTextWidth = Math.max(
        0,
        totalCellWidth - ITEM_WIDTH_WITHOUT_TEXT
      );

      const allItemsHeight =
        bodyCell.items.length === 0
          ? 0
          : BODY_CELL_CONTAINER_VERTICAL_PADDING +
            bodyCell.items.reduce(
              (sum, { componentLabel, otherLabels, urlFieldValues }) => {
                // Per-item: when a LinkIcon is rendered for items with URL
                // values, it consumes URL_ICON_OVERHEAD (icon + flex gap) on
                // the right of the top row, reducing the width available for
                // the component label. The cell-sizing pass adds
                // URL_ICON_OVERHEAD to grow the column; the wrap pass
                // subtracts it for label-line counting.
                const perItemTextWidth = Math.max(
                  1,
                  availableTextWidth -
                    (urlFieldValues?.length ? URL_ICON_OVERHEAD : 0)
                );
                const lines = Math.max(
                  1,
                  wrapString(
                    componentLabel,
                    perItemTextWidth,
                    measureText,
                    MAX_WRAPPED_LINES
                  ).length
                );
                // OtherLabel badges render below the icon+label top row and
                // span the full item content width (icon + gap + label area).
                // They are unaffected by the link icon, which lives in the
                // top row only.
                const availableBadgeTextWidth = getBodyCellBadgeTextWidth(
                  totalCellWidth,
                  contextOutlineExtent
                );
                const otherLabelsHeight =
                  otherLabels.length > 0
                    ? OTHER_LABELS_CONTAINER_TOP_PADDING +
                      otherLabels.reduce((badgeSum, otherLabel) => {
                        // Use the same word-wrap algorithm the browser uses
                        // (rather than `ceil(textWidth / availableWidth)`,
                        // which assumes characters pack perfectly across
                        // line breaks and consistently undercounts when
                        // words don't fit at the end of a line). Capped
                        // via the `maxLines` arg to match the CSS
                        // line-clamp on `OtherLabelElement`.
                        const lines = Math.max(
                          1,
                          wrapString(
                            formatOtherLabel(otherLabel),
                            availableBadgeTextWidth,
                            measureText,
                            OTHER_LABEL_MAX_LINES
                          ).length
                        );
                        return (
                          badgeSum +
                          lines * LABEL_LINE_HEIGHT +
                          OTHER_LABEL_BADGE_VERTICAL_OVERHEAD +
                          OTHER_LABELS_GAP
                        );
                      }, 0) -
                      OTHER_LABELS_GAP +
                      OTHER_LABELS_CONTAINER_BOTTOM_PADDING
                    : 0;
                return (
                  sum +
                  lines * LABEL_LINE_HEIGHT +
                  ITEM_VERTICAL_OVERHEAD +
                  otherLabelsHeight
                );
              },
              0
            ) +
            CARD_SIBLING_GAP * (bodyCell.items.length - 1) +
            BODY_CELL_CONTAINER_VERTICAL_PADDING +
            contextOutlineExtent;
      const currentCellHeight = allItemsHeight / bodyCell.rowSpan;
      for (
        let spannedRowIndex = 0;
        spannedRowIndex < bodyCell.rowSpan;
        spannedRowIndex++
      ) {
        for (
          let spannedColumnIndex = 0;
          spannedColumnIndex < bodyCell.columnSpan;
          spannedColumnIndex++
        ) {
          result[rowModelIndex + spannedRowIndex][
            bodyCell.columnIndex + spannedColumnIndex
          ] = { width: currentCellWidth, height: currentCellHeight };
        }
      }
    }
  }
  return result;
};

export const getListValueRowHeaderLineCount = (
  label: string,
  rowHeadersAreaWidth: number
): number => {
  const textAvailable = Math.max(
    0,
    rowHeadersAreaWidth - ROW_HEADER_LIST_VALUE_CELL_HORIZONTAL_OVERHEAD
  );
  return Math.max(
    1,
    wrapString(label, textAvailable, getTextWidthBold, MAX_WRAPPED_LINES).length
  );
};

const getListValueRowHeaderHeight = (
  label: string,
  rowHeadersAreaWidth: number
): number =>
  getListValueRowHeaderLineCount(label, rowHeadersAreaWidth) *
    LABEL_LINE_HEIGHT +
  LIST_VALUE_CELL_VERTICAL_OVERHEAD;

/**
 * Number of wrapped lines a component row-header card's label renders at,
 * given the actual rendered area width. Used to size the card vertically
 * (see `getComponentRowHeaderHeight`) and to switch the
 * icon/badge/expander alignment from `center` (1 line) to `flex-start`
 * (multi-line) per the Figma spec — Figma node 9260:12713, ARD-31373.
 */
export const getComponentRowHeaderLineCount = (
  group: ComponentMatrixGroup,
  level: number,
  rowHeadersAreaWidth: number
): number => {
  if (!group.label) return 1;
  // Both marginLeft and marginRight are level * CARD_GROUP_HORIZONTAL_MARGIN
  const cardWidth =
    rowHeadersAreaWidth - 2 * CARD_GROUP_HORIZONTAL_MARGIN * level;
  const hasBadge = hasChildren(group) && !group.isExpanded;
  const badgeOverhead = hasBadge ? 28 : 0;
  const textAvailable = Math.max(
    0,
    cardWidth -
      ROW_HEADER_CARD_PADDING -
      ROW_HEADER_CARD_BORDER -
      ICON_WIDTH -
      ROW_HEADER_FLEX_GAP -
      (hasChildren(group) ? EXPANDER_WIDTH + ROW_HEADER_FLEX_GAP : 0) -
      (hasBadge ? badgeOverhead + ROW_HEADER_FLEX_GAP : 0)
  );
  return Math.max(
    1,
    wrapString(group.label, textAvailable, measureText, MAX_WRAPPED_LINES)
      .length
  );
};

/**
 * Height of a component row-header card, accounting for text wrapping.
 * Uses the actual rendered area width to compute the available text width,
 * since wrapLabel is always true for row headers.
 */
export const getComponentRowHeaderHeight = (
  group: ComponentMatrixGroup,
  level: number,
  rowHeadersAreaWidth: number,
  /** Optional precomputed line count from `getComponentRowHeaderLineCount`
   * called earlier in the pipeline. Avoids re-running `wrapString` when the
   * caller already has it (e.g. `toViewpointRowModels` stashes
   * `labelLineCount` for the renderer's alignment switch and passes it
   * through). */
  precomputedLineCount?: number
): number => {
  if (!group.label) return ROW_HEADER_STRIPE_HEIGHT;
  const lines =
    precomputedLineCount ??
    getComponentRowHeaderLineCount(group, level, rowHeadersAreaWidth);
  const cardWidth =
    rowHeadersAreaWidth - 2 * CARD_GROUP_HORIZONTAL_MARGIN * level;
  // OtherLabelsContainer is a separate row below the label row, so it spans
  // the full card content width — not reduced by the count badge.
  const badgesAvailableWidth = Math.max(
    0,
    cardWidth - ROW_HEADER_CARD_PADDING - ROW_HEADER_CARD_BORDER
  );
  const otherLabelsHeight = getOtherLabelsHeight(
    group.otherLabels,
    badgesAvailableWidth
  );
  return Math.max(
    ROW_HEADER_STRIPE_HEIGHT,
    lines * LABEL_LINE_HEIGHT +
      ROW_HEADER_CARD_PADDING +
      ROW_HEADER_CARD_BORDER +
      otherLabelsHeight
  );
};

export const getHeaderRowWidth = (row: ComponentMatrixGroup) => {
  const headerValue = row.address[row.address.length - 1];
  const isComponent =
    row.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
  if (!headerValue && isComponent) {
    return 0;
  }
  const iconAndTextWidth = isComponent
    ? getTextWidth(row.label ?? '') + ICON_WIDTH
    : getTextWidthBold(headerValue ?? NONE_HEADER_LABEL) +
      ROW_HEADER_LIST_VALUE_CELL_HORIZONTAL_OVERHEAD;

  const hasBadge = isComponent && hasChildren(row) && !row.isExpanded;
  const badgeOverhead = hasBadge ? 28 + ROW_HEADER_FLEX_GAP : 0;
  const labelRowWidth =
    iconAndTextWidth + (hasChildren(row) ? EXPANDER_WIDTH : 0) + badgeOverhead;

  // OtherLabel badges render on their own row below the label and span the full
  // card content width, so the row header must be wide enough to fit the widest
  // badge — otherwise the pre-computed wrapped lines break the badge text
  // character-by-character (ARD-33206). Mirrors getHeaderColumnWidth.
  const widestOtherLabel =
    isComponent && row.otherLabels.length > 0
      ? row.otherLabels.reduce(
          (max, label) =>
            Math.max(
              max,
              getTextWidth(formatOtherLabel(label)) +
                OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
            ),
          0
        )
      : 0;

  return Math.max(labelRowWidth, widestOtherLabel);
};
const getHeaderColumnWidth = (column: ComponentMatrixGroup) => {
  const headerValue = column.address[column.address.length - 1];
  if (column.isHidden || headerValue === COMPONENT_HIERARCHY_PADDING) {
    return 0;
  }

  const expanderWidth = hasChildren(column) ? EXPANDER_WIDTH : 0;
  const indentation = (column.address.length - 1) * (MARGIN_PER_LEVEL * 2);
  const isComponent =
    column.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
  const iconWidth = isComponent ? ICON_WIDTH : 0;

  const headerLabel = headerValue === null ? NONE_HEADER_LABEL : headerValue;
  const headerLabelWidth = isComponent
    ? getTextWidth(column.label ?? '')
    : getTextWidthBold(headerLabel) +
      LIST_VALUE_COLUMN_HEADER_HORIZONTAL_OVERHEAD;

  // Root-level columns have BODY_CELL_CONTAINER_HORIZONTAL_MARGIN on each side.
  // Child component columns (level >= 1) have left+right margins that consume
  // column track space — include them so the card content is never clipped.
  // Non-first children use CARD_COLUMN_SIBLING_GAP on the left; first children
  // use CARD_COLUMN_GROUP_LEFT_MARGIN — both are 8px so we use either here.
  // Last children also add CARD_COLUMN_GROUP_RIGHT_MARGIN on the right.
  // We conservatively include both since sibling position is unknown here.
  const isRootLevel = column.address.length === 1;
  const isChildComponent = isComponent && !isRootLevel;
  const horizontalMargin = isRootLevel
    ? BODY_CELL_CONTAINER_HORIZONTAL_MARGIN * 2
    : isChildComponent
      ? CARD_COLUMN_SIBLING_GAP + CARD_COLUMN_GROUP_RIGHT_MARGIN
      : 0;

  // All component column cards have left+right padding inside the stripe.
  const cardPadding = isComponent ? COLUMN_HEADER_CARD_HORIZONTAL_PADDING : 0;

  // Component columns with children render a CountBadge after the label.
  // The flex container (gap: s4 = 4px) has 3 gaps: [expander, icon, label, badge].
  // ICON_WIDTH (20) overestimates the actual 16px ComponentIconContainer by 4px.
  // EXPANDER_WIDTH (20 = IconSize.MEDIUM) matches the expander's actual rendered
  // width — IconClickable passes containerStyle={{ fontSize: iconSize }} but Icon
  // still uses iconSize = IconSize.MEDIUM = 20 by default. So only the icon
  // overestimate covers 1 of the 3 gaps (4px). The remaining 2 gaps (8px) +
  // badge min-width (20px) = 28px are unaccounted for.
  const badgeOverhead =
    isComponent && hasChildren(column) && !column.isExpanded ? 28 : 0;

  const labelRowWidth =
    headerLabelWidth +
    indentation +
    iconWidth +
    expanderWidth +
    badgeOverhead +
    COLUMN_HEADER_HORIZONTAL_BORDER +
    horizontalMargin +
    cardPadding;

  // OtherLabelsContainer spans the full card content width, so the card must
  // be wide enough to fit the widest badge text without severe wrapping.
  const widestBadgeCardWidth =
    isComponent && column.otherLabels.length > 0
      ? column.otherLabels.reduce(
          (max, label) =>
            Math.max(
              max,
              getTextWidth(formatOtherLabel(label)) +
                OTHER_LABEL_BADGE_HORIZONTAL_OVERHEAD
            ),
          0
        ) +
        COLUMN_HEADER_CARD_HORIZONTAL_PADDING +
        COLUMN_HEADER_HORIZONTAL_BORDER +
        horizontalMargin
      : 0;

  return Math.max(labelRowWidth, widestBadgeCardWidth);
};

export const getHeaderColumnWidths = (
  viewModel: ViewpointModeComponentMatrixViewModel
) => {
  const { columnsByDepthWithGridInfo } = viewModel;
  // Total tracks = sum of colSpans of root-level groups (depth 0).
  const totalTracks = (columnsByDepthWithGridInfo[0] ?? []).reduce(
    (sum, col) => sum + col.colSpan,
    0
  );
  const result = range(totalTracks).map(returnZero);

  for (const levelColumns of columnsByDepthWithGridInfo) {
    for (const column of levelColumns) {
      const rawWidth = getHeaderColumnWidth(column);
      // Hidden/padding columns (rawWidth === 0) must not get the MIN floor —
      // they render nothing, and applying 36px to each of N hidden leaf columns
      // in a collapsed group would allocate 36px × N instead of the group's
      // actual label width.
      const currentColumnWidth =
        rawWidth === 0
          ? 0
          : Math.min(
              MAX_COLUMN_HEADER_WIDTH,
              Math.max(MIN_COLUMN_HEADER_WIDTH, rawWidth)
            );
      const perColumnWidth = currentColumnWidth / column.colSpan;
      // column.gridColumn is 1-based; result is 0-based
      for (let i = 0; i < column.colSpan; i++) {
        result[column.gridColumn - 1 + i] = Math.max(
          result[column.gridColumn - 1 + i],
          perColumnWidth
        );
      }
    }
  }
  return result;
};

/**
 * Leaf-track flags marking hierarchy-padding columns. An empty padding column
 * collapses to a thin stripe (ARD-32761); one holding a parent's direct members
 * keeps its width. Emptiness is decided in `measure` from the measured body
 * width, so this only marks which tracks are padding.
 */
export const getPaddingTracks = (
  viewModel: ViewpointModeComponentMatrixViewModel
): boolean[] => {
  const { columnsByDepthWithGridInfo } = viewModel;
  const totalTracks = (columnsByDepthWithGridInfo[0] ?? []).reduce(
    (sum, col) => sum + col.colSpan,
    0
  );
  const isPaddingTrack = range(totalTracks).map(() => false);
  for (const levelColumns of columnsByDepthWithGridInfo) {
    for (const column of levelColumns) {
      if (column.address.at(-1) === COMPONENT_HIERARCHY_PADDING) {
        isPaddingTrack[column.gridColumn - 1] = true;
      }
    }
  }
  return isPaddingTrack;
};

export const computeRawRowHeaderWidth = (rowModel: {
  headerCells: HeaderCellModel[];
}): number =>
  rowModel.headerCells.reduce((maxWidth, headerCell) => {
    if (
      headerCell.group.isHidden ||
      headerCell.group.label === COMPONENT_HIERARCHY_PADDING
    ) {
      return maxWidth;
    }
    // Use CM grid position (indexAddress) not component tree depth (address) so
    // the margin subtracted here matches the CSS margin applied by getRowHeaderMargins.
    const level = headerCell.indexAddress.length - 1;
    const isComponentRow =
      headerCell.group.ownerType ===
      ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
    const cardOverhead = isComponentRow
      ? COMPONENT_ROW_HEADER_CARD_OVERHEAD
      : 0;
    return Math.max(
      maxWidth,
      2 * CARD_GROUP_HORIZONTAL_MARGIN * level +
        getHeaderRowWidth(headerCell.group) +
        cardOverhead
    );
  }, 0);

/**
 * Pre-render width of the row-headers area: the widest raw row-header width,
 * clamped to `MAX_ROW_HEADER_WIDTH`. Used by `getComponentRowHeaderHeight` /
 * `getListValueRowHeaderHeight` to wrap labels at the real rendered card
 * width before the grid track is sized in `measureRowHeaders`.
 */
export const clampRowHeadersAreaWidth = (rawWidths: number[]): number =>
  rawWidths.length === 0
    ? 0
    : Math.min(MAX_ROW_HEADER_WIDTH, Math.max(...rawWidths));

export const measureRowHeaders = (
  rowModels: RowElementModel[],
  overrideWidth?: number
) => {
  const rowHeadersAreaWidth =
    overrideWidth ??
    clampRowHeadersAreaWidth(rowModels.map(computeRawRowHeaderWidth));

  return rowModels.map(rowModel => {
    const baseSize = rowModel.headerCells.reduce(
      (size: Size, headerCell) => {
        if (
          headerCell.group.isHidden ||
          headerCell.group.label === COMPONENT_HIERARCHY_PADDING
        ) {
          return size;
        }
        // Use CM grid position (indexAddress) not component tree depth (address)
        // so the level here matches the CSS margins from getRowHeaderMargins.
        const level = headerCell.indexAddress.length - 1;

        const isComponentRow =
          headerCell.group.ownerType ===
          ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
        const isLeafOrCollapsedComponent =
          isComponentRow &&
          (!hasChildren(headerCell.group) || !headerCell.group.isExpanded);
        // Component rows (all levels, leaf/collapsed or expanded) contribute zero
        // vertical padding to the row height — the CSS margins in getRowHeaderMargins
        // handle visual insets for the GroupOutline staircase without inflating rows.
        // List-value rows still apply padding so their borders are contained.
        const { topPadding, bottomPadding } =
          level === 0
            ? { topPadding: 0, bottomPadding: 0 }
            : isComponentRow
              ? {
                  topPadding: CARD_GROUP_TOP_MARGIN,
                  bottomPadding: 0,
                }
              : {
                  topPadding: isFirstChild(headerCell)
                    ? calculateRowHeaderTopOffset(headerCell.indexAddress)
                    : 2 * HEADER_CELL_VERTICAL_PADDING,
                  bottomPadding:
                    HEADER_CELL_VERTICAL_PADDING +
                    (isLastChild(headerCell)
                      ? calculateRowHeaderBottomOffset(level)
                      : 0),
                };
        const itemHeight = isComponentRow
          ? getComponentRowHeaderHeight(
              headerCell.group,
              level,
              rowHeadersAreaWidth,
              // Reuse the precomputed line count only when measuring at the
              // auto width (no override). When `overrideWidth` is set the
              // user has resized the row-header area, so the precomputed count
              // (from `toViewpointRowModels` at the auto width) may be stale —
              // let the function recompute at the current `rowHeadersAreaWidth`.
              overrideWidth === undefined
                ? headerCell.labelLineCount
                : undefined
            )
          : getListValueRowHeaderHeight(
              headerCell.group.address.at(-1) ?? NONE_HEADER_LABEL,
              rowHeadersAreaWidth
            );
        // Last-sibling leaf/collapsed component at any level > 0: extend the row
        // so there is 8px breathing room inside the innermost GroupOutline, then
        // each outer border adds another 8px step (the staircase effect).
        const staircaseGap =
          isLeafOrCollapsedComponent && level > 0 && isLastChild(headerCell)
            ? level * CARD_GROUP_BOTTOM_MARGIN
            : 0;
        const minHeight =
          Math.max(
            MIN_HEADER_CELL_HEIGHT,
            itemHeight + topPadding + bottomPadding
          ) + staircaseGap;

        const nextHeight = Math.max(
          minHeight,
          size.height + (isComponentRow ? 0 : ROW_HEADER_LABEL_HEIGHT)
        );
        const cardOverhead = isComponentRow
          ? COMPONENT_ROW_HEADER_CARD_OVERHEAD
          : 0;
        return {
          height: nextHeight,
          width: Math.max(
            size.width,
            2 * CARD_GROUP_HORIZONTAL_MARGIN * level +
              getHeaderRowWidth(headerCell.group) +
              cardOverhead
          ),
        };
      },
      { width: 0, height: 0 }
    );

    return {
      width: baseSize.width,
      height: baseSize.height,
    };
  });
};

export const measureRowLevelHeaders = (levelHeaders: string[]) =>
  levelHeaders.reduce(
    (width: number, levelHeader: string, index: number) =>
      Math.max(
        width,
        measureLevelLabel(levelHeader) +
          index * CARD_GROUP_HORIZONTAL_MARGIN +
          LEVEL_HEADER_CELL_HORIZONTAL_PADDING * 2
      ),
    0
  );

/** Non-text horizontal overhead for a column header card (mirrors getHeaderColumnWidth). */
const getColumnHeaderOverhead = (column: ComponentMatrixGroup): number => {
  const expanderWidth = hasChildren(column) ? EXPANDER_WIDTH : 0;
  const indentation = (column.address.length - 1) * (MARGIN_PER_LEVEL * 2);
  const isComponent =
    column.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
  const iconWidth = isComponent ? ICON_WIDTH : 0;
  const isRootLevel = column.address.length === 1;
  const isChildComponent = isComponent && !isRootLevel;
  const horizontalMargin = isRootLevel
    ? BODY_CELL_CONTAINER_HORIZONTAL_MARGIN * 2
    : isChildComponent
      ? CARD_COLUMN_SIBLING_GAP + CARD_COLUMN_GROUP_RIGHT_MARGIN
      : 0;
  const cardPadding = isComponent ? COLUMN_HEADER_CARD_HORIZONTAL_PADDING : 0;
  const badgeOverhead =
    isComponent && hasChildren(column) && !column.isExpanded ? 28 : 0;
  return (
    indentation +
    iconWidth +
    expanderWidth +
    badgeOverhead +
    COLUMN_HEADER_HORIZONTAL_BORDER +
    horizontalMargin +
    cardPadding
  );
};

/** Number of wrapped lines for a column header label (1 when no wrapping needed). */
const getColumnHeaderLabelLineCount = (
  column: ComponentMatrixGroup
): number => {
  if (
    column.isHidden ||
    column.label === COMPONENT_HIERARCHY_PADDING ||
    column.ownerType !== ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT ||
    !column.label
  ) {
    return 1;
  }
  const rawWidth = getHeaderColumnWidth(column);
  if (rawWidth <= MAX_COLUMN_HEADER_WIDTH) return 1;
  const textAvailable = Math.max(
    0,
    MAX_COLUMN_HEADER_WIDTH - getColumnHeaderOverhead(column)
  );
  return Math.max(
    1,
    wrapString(column.label, textAvailable, measureText, MAX_WRAPPED_LINES)
      .length
  );
};

const getColumnHeaderOtherLabelsHeight = (
  column: ComponentMatrixGroup
): number => {
  if (
    column.isHidden ||
    column.ownerType !== ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT ||
    column.otherLabels.length === 0
  ) {
    return 0;
  }
  const clampedWidth = Math.min(
    MAX_COLUMN_HEADER_WIDTH,
    Math.max(MIN_COLUMN_HEADER_WIDTH, getHeaderColumnWidth(column))
  );
  // OtherLabelsContainer is a separate row below the label row, so it spans
  // the full card content width — not reduced by icon/expander/count-badge.
  const textAvailable = Math.max(
    0,
    clampedWidth -
      COLUMN_HEADER_CARD_HORIZONTAL_PADDING -
      COLUMN_HEADER_HORIZONTAL_BORDER
  );
  return getOtherLabelsHeight(column.otherLabels, textAvailable);
};

/**
 * Per-level stripe height accounting for text wrapping.
 * Exported so measurements can thread it to ComponentMatrixHeaderCell for GroupOutline positioning.
 */
export const getColumnHeaderLevelStripeHeights = (
  columnsByDepth: ComponentMatrixGroup[][]
): number[] =>
  columnsByDepth.map(level => {
    const maxLines = level.reduce(
      (max, col) => Math.max(max, getColumnHeaderLabelLineCount(col)),
      1
    );
    const maxOtherLabelsHeight = level.reduce(
      (max, col) => Math.max(max, getColumnHeaderOtherLabelsHeight(col)),
      0
    );
    return Math.max(
      MIN_HEADER_CELL_HEIGHT,
      COLUMN_HEADER_VERTICAL_PADDING +
        maxLines * LABEL_LINE_HEIGHT +
        maxOtherLabelsHeight
    );
  });

export const getMaxWrappedLinesForColumn = (
  col: ComponentMatrixGroup
): number => {
  const headerValue = col.address.at(-1);
  const label = headerValue ?? NONE_HEADER_LABEL;
  const rawWidth = getHeaderColumnWidth(col);
  const textAvailable = Math.max(
    0,
    MAX_COLUMN_HEADER_WIDTH - LIST_VALUE_COLUMN_HEADER_HORIZONTAL_OVERHEAD
  );
  const shouldWrap =
    !col.isHidden &&
    headerValue !== COMPONENT_HIERARCHY_PADDING &&
    rawWidth > MAX_COLUMN_HEADER_WIDTH;
  return shouldWrap
    ? wrapString(label, textAvailable, getTextWidthBold, MAX_WRAPPED_LINES)
        .length
    : 1;
};

const getHeaderLevelHeight = (level: ComponentMatrixGroup[]): number => {
  const maxLines = level.reduce(
    (max, col) => Math.max(max, getMaxWrappedLinesForColumn(col)),
    1
  );
  return Math.max(
    LEVEL_HEADER_CARD_HEIGHT,
    maxLines * LABEL_LINE_HEIGHT + LIST_VALUE_CELL_VERTICAL_OVERHEAD
  );
};

const getStripeLevelHeight = (
  stripeHeights: number[],
  lastComponentLevelIndex: number,
  numComponentLevels: number,
  levelIndex: number
): number => {
  const stripeHeight = stripeHeights[levelIndex];
  const topMargin = levelIndex === 0 ? 0 : CARD_COLUMN_GROUP_TOP_MARGIN;
  const bottomMargin =
    levelIndex === lastComponentLevelIndex
      ? (numComponentLevels - 1) * CARD_COLUMN_GROUP_BOTTOM_MARGIN
      : 0;
  return stripeHeight + topMargin + bottomMargin;
};

/**
 * Returns the grid row height (in px) for each column header depth level.
 *
 * Component levels use a system where GroupOutline bottom-stagger (8px per
 * nesting level) is encoded purely in the `groupOutlineBottom` CSS value; the
 * row heights themselves provide only the stripe height (44px) plus the top
 * margin (8px) for child levels. The last *visible component* level adds extra
 * height to accommodate the stagger of all ancestor GroupOutlines:
 *   last component level = stripe (44) + top margin (8) + (numComponentLevels-1) * bottom margin (8)
 *
 * "Last visible component level" is the deepest index where hasComponent is
 * true. columnsByDepth may contain many trailing levels with no visible
 * component groups (because the dataset hierarchy is deeper than what's
 * currently expanded); those levels return LEVEL_HEADER_CARD_HEIGHT but are
 * collapsed to 0px by columnHeadersAreaGridTemplateRows so they don't
 * contribute to the rendered height — and they must NOT be treated as the
 * "last" level for stagger purposes.
 *
 * - Root-level (level 0) component rows: COLUMN_HEADER_STRIPE_HEIGHT (44px)
 * - Intermediate child component rows: stripe + top margin (52px)
 * - Last visible child component row: stripe + top margin + (numComponentLevels-1) * bottom margin
 * - List-value rows at any level: LEVEL_HEADER_CARD_HEIGHT (to fit two-line label card)
 */
export const getColumnHeaderLevelHeights = (
  columnsByDepth: ComponentMatrixGroup[][]
): number[] => {
  const hasComponentPerLevel = columnsByDepth.map(level =>
    level.some(
      col =>
        col.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT &&
        !col.isHidden &&
        col.label !== COMPONENT_HIERARCHY_PADDING
    )
  );
  // Find the deepest level that has visible component content — this is the
  // level whose row needs to absorb the GroupOutline bottom stagger.
  const lastComponentLevelIndex = hasComponentPerLevel.reduceRight(
    (found, has, i) => (found === -1 && has ? i : found),
    -1
  );
  // The number of visible component levels determines the total stagger height.
  const numComponentLevels = lastComponentLevelIndex + 1;

  const stripeHeights = getColumnHeaderLevelStripeHeights(columnsByDepth);

  return columnsByDepth.map((level, levelIndex) =>
    hasComponentPerLevel[levelIndex]
      ? getStripeLevelHeight(
          stripeHeights,
          lastComponentLevelIndex,
          numComponentLevels,
          levelIndex
        )
      : getHeaderLevelHeight(level)
  );
};

export const levelHasRenderableContent = (level: ComponentMatrixGroup[]) =>
  level.some(
    header =>
      !header.isHidden &&
      header.label !== COMPONENT_HIERARCHY_PADDING &&
      header.ownerType !== ComponentMatrixGroupOwnerType.EMPTY
  );

/**
 * Returns the CSS grid-template-rows string for the column headers area,
 * using per-level heights from getColumnHeaderLevelHeights. The LAST
 * renderable level uses `minmax(<h>, 1fr)` so it absorbs any extra height
 * when the column-headers area is shorter than the level-headers area
 * (the outer grid's row 1 sizes to the taller of the two; the 1fr lets the
 * deepest header level stretch to fill — ARD-31376).
 */
export const columnHeadersAreaGridTemplateRows = (
  columnsByDepth: ComponentMatrixGroup[][],
  levelHeights: number[]
) => {
  const renderableMask = columnsByDepth.map(levelHasRenderableContent);
  const lastRenderableIndex = renderableMask.lastIndexOf(true);
  return columnsByDepth
    .map((_level, levelIndex) => {
      if (!renderableMask[levelIndex]) return '0';
      const height = levelHeights[levelIndex];
      return levelIndex === lastRenderableIndex
        ? `minmax(${height}px, 1fr)`
        : `${height}px`;
    })
    .join(' ');
};
